/**
 * STATUS BANNER COMPONENT
 * Success/Error/Warning message banner
 * @version 1.0.0
 * @production-ready
 */

import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

export type StatusType = 'success' | 'error' | 'warning' | 'info';

export interface StatusBannerProps {
  status: StatusType | null;
  message: string;
  onClose?: () => void;
  autoDismiss?: boolean;
  dismissDelay?: number;
  showIcon?: boolean;
  showCloseButton?: boolean;
}

export const StatusBanner: React.FC<StatusBannerProps> = ({
  status,
  message,
  onClose,
  autoDismiss = true,
  dismissDelay = 3000,
  showIcon = true,
  showCloseButton = true
}) => {
  // Auto dismiss
  useEffect(() => {
    if (autoDismiss && status && onClose) {
      const timer = setTimeout(onClose, dismissDelay);
      return () => clearTimeout(timer);
    }
  }, [autoDismiss, status, dismissDelay, onClose]);

  if (!status || !message) return null;

  const config = {
    success: {
      bgColor: 'var(--color-status-success-light)',
      textColor: 'var(--color-status-success)',
      borderColor: 'var(--color-status-success)',
      icon: CheckCircle
    },
    error: {
      bgColor: 'var(--color-status-error-light)',
      textColor: 'var(--color-status-error)',
      borderColor: 'var(--color-status-error)',
      icon: XCircle
    },
    warning: {
      bgColor: 'var(--color-status-warning-light)',
      textColor: 'var(--color-status-warning)',
      borderColor: 'var(--color-status-warning)',
      icon: AlertTriangle
    },
    info: {
      bgColor: 'var(--color-status-info-light)',
      textColor: 'var(--color-status-info)',
      borderColor: 'var(--color-status-info)',
      icon: Info
    }
  };

  const { bgColor, textColor, borderColor, icon: Icon } = config[status];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        className="flex items-center gap-2 px-4 py-2 text-sm border-b"
        style={{
          background: bgColor,
          color: textColor,
          borderBottomColor: borderColor
        }}
      >
        {showIcon && <Icon size={16} />}
        
        <span className="flex-1 font-medium">{message}</span>
        
        {showCloseButton && onClose && (
          <button
            onClick={onClose}
            className="hover:opacity-70 transition-opacity"
            style={{ color: textColor }}
            title="Dismiss"
          >
            <X size={16} />
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
